import { useMemo, useEffect } from 'react';
import { useFetcher } from 'react-router';
import { useRevalidator } from 'react-router';

const POLL_INTERVAL_MS = 10000;
const PENDING_STATUSES = ['starting', 'ingesting', 'syncing'];

/**
 * Custom hook for polling email jobs that are in pending states.
 * 
 * This hook manages automatic polling of email jobs when any job is in a pending state
 * ('starting', 'ingesting', or 'syncing'). It combines data from both the initial loader
 * and subsequent poller fetcher calls, providing a unified interface for job data.
 * 
 * Features:
 * - Automatically polls every 10 seconds when jobs are pending
 * - Merges data from loader and poller fetcher sources
 * 
 * @param {Object} loaderJobs - Initial jobs data from route loader
 * @param {Object} initialQueryParams - Initial query parameters for the jobs request
 * @returns {Array} jobs - Array of job objects from either poller or loader
 * @returns {number} jobCount - Total count of jobs
 * @returns {Object} queryParams - Query parameters used for the jobs request
 */
export const useEmailJobsPoller = ({ loaderJobs, initialQueryParams }) => {
	const revalidator = useRevalidator();
	const pollerFetcher = useFetcher();

	// handle jobs from either poller or loader; memoized to prevent re-renders
	const jobs = useMemo(
		() => pollerFetcher.data?.jobs?.jobs || loaderJobs.jobs || [],
		[pollerFetcher.data, loaderJobs]
	);

	const hasAnyJobPending =
		Array.isArray(jobs) &&
		jobs.some((j) => PENDING_STATUSES.includes(j.status));

	const jobCount = pollerFetcher.data?.jobs?.count ?? loaderJobs.count ?? 0;

	const jobStatistics =
		pollerFetcher.data?.jobs?.statistics ?? loaderJobs.statistics ?? {};

	const finalQueryParams =
		pollerFetcher.data?.jobs?.parameters ??
		loaderJobs.parameters ??
		initialQueryParams;

	// Conditional polling for jobs in 'starting', 'ingesting' or 'syncing' state
	useEffect(() => {
		if (!hasAnyJobPending) return;

		const intervalId = setInterval(() => {
			revalidator.revalidate();
		}, POLL_INTERVAL_MS);

		return () => clearInterval(intervalId);
	}, [hasAnyJobPending, revalidator]);

	return { jobs, jobStatistics, jobCount, queryParams: finalQueryParams };
};
